import type { JSX } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useStudentProgress } from '@/features/memorization/hooks';
import { ProgressStatsGrid } from '@/features/memorization/progress-stats-grid';

/**
 * "Progress" (Phase G-1, Job 16) - `GET /memorization/progress/:studentId`'s
 * own authoritative, live-computed numbers, rendered as-is. No alternative
 * progress metric is calculated client-side. Average scores are shown
 * unredacted for every caller, including a LIMITED_READ Guardian: ADR-025's
 * Addendum only redacts individual Evaluation segment scores/notes and
 * Murajaah session notes, never this aggregate (`progress-summary.service.ts`
 * applies no LIMITED_READ redaction at all).
 */
export function ProgressSection({
  studentId,
  mode = 'assigned',
}: {
  readonly studentId: string;
  /** `personal` renames the assigned tile - see `ProgressStatsGrid`. */
  readonly mode?: 'assigned' | 'personal';
}): JSX.Element {
  const { data, isLoading, isError } = useStudentProgress(studentId);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Progress</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {isLoading && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        )}

        {isError && (
          <Alert variant="destructive">
            <AlertDescription>Gagal memuat progres.</AlertDescription>
          </Alert>
        )}

        {!isLoading && !isError && data !== undefined && (
          <ProgressStatsGrid
            data={data}
            assignedLabel={mode === 'personal' ? 'Belum disetorkan' : 'Ditugaskan'}
          />
        )}
      </CardContent>
    </Card>
  );
}
